export default class EventTrackerManager {
  constructor() {
    this._trackers = new Map()
    this._langCode = null
  }

  _getTrackers() {
    return this._trackers
  }

  _getTracker(trackerName) {
    return this._getTrackers().get(trackerName)
  }

  async _addTracker(trackerName) {
    if (!this._trackers.has(trackerName)) {
      const trackerImport = await import(
        /* webpackChunkName: "tracker-[request]" */
        /* webpackPreload: true */
        `./trackers/${trackerName}`)

      // eslint-disable-next-line new-cap
      this._trackers.set(trackerName, new trackerImport.default())
    }
  }

  _getLangCode() {
    // get code from html tag or <div id="langCode"> if it exists (added by WPML plugin)
    if (!this._langCode) {
      const offset = 2
      this._langCode = document.getElementsByTagName('html')[0].lang.substring(0, offset).toUpperCase()
      const iclLanguageCodeEl = document.getElementById('langCode')
      if (iclLanguageCodeEl) {
        this._langCode = iclLanguageCodeEl.innerHTML.toUpperCase()
      }
    }

    return this._langCode
  }

  _sendEvent(...args) {
    for (const tracker of this._getTrackers().values()) {
      tracker.sendEvent(...args)
    }
  }

  _addListeners() {
    this._addServicesLoadingListeners()
    this._addFormsSubmitListeners()

    // handle data-ga-XXX elements after a small delay (to allow adding these attributes through JavaScript)
    const dataApiTimeout = 250
    setTimeout(() => {
      this._addDataApiListeners()
    }, dataApiTimeout)
  }

  _addServicesLoadingListeners() {
    const events = [
      {
        eventName: 'rsq.service.googleanalyticsgtag.ready',
        trackerName: 'analytics'
      },
      {
        eventName: 'rsq.service.piwik.ready',
        trackerName: 'piwik'
      }
    ]

    events.forEach((info) => {
      document.addEventListener(info.eventName, () => {
        this._addTracker(info.trackerName)
      })
    })
  }

  _addFormsSubmitListeners() {
    // handle Contact Form 7 submit
    document.addEventListener('wpcf7mailsent', (event) => {
      let action = ''
      const apiResponseInto = event.detail?.apiResponse?.into
      if (typeof apiResponseInto !== 'undefined') {
        action = apiResponseInto.replace('#', '')
        const targetedCfForm = document.getElementById(action).querySelector('.wpcf7-form').name
        if (typeof targetedCfForm !== 'undefined') {
          action = targetedCfForm
        }
      }

      // set default event params
      const eventParams = {
        category: 'Formulaire',
        action,
        label: this._getLangCode()
      }

      // allow overriding default params with inline data attributes
      // note: for consistency, we're using the same attribute names as Data API (data-ga-event-XXX)
      const formEl = event.target
      if (formEl) {
        for (const key of Object.keys(eventParams)) {
          const attrName = this._getDataApiAttributeName(key)
          if (formEl.hasAttribute(attrName)) {
            eventParams[key] = this._getDataApiAttributeValue(formEl, key)
          }
        }
      }

      this._sendEvent(eventParams)
    })
  }

  _addDataApiListeners() {
    // check if we have at least one matching element (because querySelectorAll is slow)
    const onAttrName = this._getDataApiAttributeName('on')
    const selector = `[${onAttrName}]`
    const hasAnyElement = document.querySelector(selector) !== null
    if (hasAnyElement) {
      const elements = document.querySelectorAll(selector)
      elements.forEach((element) => {
        const eventName = this._getDataApiAttributeValue(element, 'on')
        element.addEventListener(eventName, () => {
          // get inline event params
          const paramKeys = ['category', 'action', 'label']
          const eventParams = {}
          paramKeys.forEach((paramKey) => {
            const value = this._getDataApiAttributeValue(element, paramKey)
            if (value && value.length) {
              eventParams[paramKey] = value
            }
          })

          // send event if we have at least a category and an action
          if (typeof eventParams.category !== 'undefined' && typeof eventParams.action !== 'undefined') {
            this._sendEvent(eventParams)
          }
        }, {
          passive: true
        })
      })
    }
  }

  /* DATA API */

  _getParsedDataApiValue(value, type = 'string') {
    switch (type) {
      case 'int':
      case 'integer':
        return parseInt(value, 10)
      case 'float':
        return parseFloat(value)
      default:
        return String(value)
    }
  }

  _getDataApiAttributeName(type) {
    return `data-ga-event-${type}`
  }

  _getDataApiAttributeValue(el, type) {
    const attrName = this._getDataApiAttributeName(type)

    return this._getParsedDataApiValue(el.getAttribute(attrName))
  }

  /* HELPERS */

  static get _instance() {
    const instanceName = this._getEventTrackerInstanceName()
    if (!(instanceName in window)) {
      return null
    }

    return window[instanceName]
  }

  static getTrackers() {
    return this._instance._getTrackers()
  }

  static getTracker(trackerName) {
    return this._instance._getTracker(trackerName)
  }

  static addTracker(trackerName) {
    this._instance._addTracker(trackerName)
  }

  static sendEvent(...args) {
    this._instance._sendEvent(...args)
  }

  /* INTERNAL METHODS */

  static _getEventTrackerInstanceName() {
    // keep a simple instance name for external calls
    return '_net'
  }

  static _init() {
    const instanceName = this._getEventTrackerInstanceName()
    if (typeof window[instanceName] === 'undefined') {
      window[instanceName] = new this()
      window[instanceName]._addListeners()
    }
  }
}
